// src/screens/ProfileScreen.js
import React, { useContext, useState, useEffect, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import ThemeToggle from '../components/ThemeToggle';
import { usersAPI } from '../services/api';

const ProfileScreen = () => {
  const { user, logout, updateUser } = useContext(AuthContext);
  const navigate = useNavigate();

  // Edit profile state
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState('');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameError, setNameError] = useState('');

  // Phone edit state (required for dealership follow-up on test drives)
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Document state
  const [licenseDoc, setLicenseDoc] = useState(null);
  const [insuranceDoc, setInsuranceDoc] = useState(null);
  const [docLoading, setDocLoading] = useState({ license: false, insurance: false });
  const [docError, setDocError] = useState({ license: '', insurance: '' });
  const licenseInputRef = useRef(null);
  const insuranceInputRef = useRef(null);

  // Load user data and documents on mount
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setPhone(user.phone || '');
      loadDocuments();
    }
  }, [user]);

  const loadDocuments = async () => {
    try {
      const [licenseRes, insuranceRes] = await Promise.allSettled([
        usersAPI.getDocument('license'),
        usersAPI.getDocument('insurance'),
      ]);
      if (licenseRes.status === 'fulfilled') {
        setLicenseDoc(licenseRes.value.data);
      }
      if (insuranceRes.status === 'fulfilled') {
        setInsuranceDoc(insuranceRes.value.data);
      }
    } catch (e) {
      // Documents may not exist, that's okay
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Account deletion. Password accounts confirm with their password;
  // accounts that sign in with Google/Apple/Facebook type DELETE.
  const usesPassword = !user?.authProvider || user.authProvider === 'local';
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleting(true);
    setDeleteError('');
    try {
      await usersAPI.deleteAccount(usesPassword ? { password: deleteInput } : { confirm: 'DELETE' });
      await logout();
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Could not delete your account. Please try again.');
      setDeleting(false);
    }
  };

  const handleNameSave = async () => {
    if (!name.trim()) {
      setNameError('Name cannot be empty');
      return;
    }
    setNameLoading(true);
    setNameError('');
    try {
      const { data } = await usersAPI.updateProfile({ name: name.trim() });
      updateUser({ name: data.name });
      setIsEditingName(false);
    } catch (e) {
      setNameError(e.response?.data?.message || 'Failed to update name');
    } finally {
      setNameLoading(false);
    }
  };

  const handlePhoneSave = async () => {
    if (!phone.trim()) {
      setPhoneError('Phone number cannot be empty');
      return;
    }
    setPhoneLoading(true);
    setPhoneError('');
    try {
      const { data } = await usersAPI.updateProfile({ phone: phone.trim() });
      updateUser({ phone: data.phone });
      setIsEditingPhone(false);
    } catch (e) {
      setPhoneError(e.response?.data?.message || 'Failed to update phone number');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      await usersAPI.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (e) {
      setPasswordError(e.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDocumentUpload = async (type, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setDocError(prev => ({ ...prev, [type]: 'Invalid file type. Please upload an image or PDF.' }));
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setDocError(prev => ({ ...prev, [type]: 'File size must be less than 10MB.' }));
      return;
    }

    setDocLoading(prev => ({ ...prev, [type]: true }));
    setDocError(prev => ({ ...prev, [type]: '' }));

    try {
      const { data } = await usersAPI.uploadDocument(type, file);
      if (type === 'license') {
        setLicenseDoc(data.document);
      } else {
        setInsuranceDoc(data.document);
      }
    } catch (e) {
      setDocError(prev => ({
        ...prev,
        [type]: e.response?.data?.message || 'Failed to upload document',
      }));
    } finally {
      setDocLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // Fetch the file with the session header and open it from memory, so the
  // login token never appears in a URL. The tab opens immediately (inside the
  // click) so pop-up blockers allow it, and is filled once the file arrives.
  const handleDocumentView = async (type) => {
    const tab = window.open('', '_blank');
    try {
      const res = await usersAPI.getDocumentFile(type);
      const url = URL.createObjectURL(res.data);
      if (tab) tab.location = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (tab) tab.close();
      setDocError((prev) => ({ ...prev, [type]: 'Could not open the document' }));
    }
  };

    const handleDocumentDelete = async (type) => {
    if (!window.confirm(`Are you sure you want to delete your ${type} document?`)) {
      return;
    }

    setDocLoading(prev => ({ ...prev, [type]: true }));
    try {
      await usersAPI.deleteDocument(type);
      if (type === 'license') {
        setLicenseDoc(null);
      } else {
        setInsuranceDoc(null);
      }
    } catch (e) {
      setDocError(prev => ({
        ...prev,
        [type]: e.response?.data?.message || 'Failed to delete document',
      }));
    } finally {
      setDocLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const getVerificationBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            Verified
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            Pending Review
          </span>
        );
    }
  };

  const DocumentCard = ({ type, document, loading, error, inputRef }) => {
    const title = type === 'license' ? "Driver's License" : 'Insurance Card';
    const icon = type === 'license' ? '🪪' : '📋';

    return (
      <div className="bg-white dark:bg-app-bg-secondary rounded-2xl p-5 border border-app-border-light/50 dark:border-app-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{icon}</span>
            <h4 className="font-semibold text-app-text-light dark:text-app-text">{title}</h4>
          </div>
          {document && getVerificationBadge(document.verificationStatus)}
        </div>

        {error && (
          <div className="mb-3 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {document ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-app-bg-light dark:bg-app-bg rounded-xl">
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="w-10 h-10 rounded-lg bg-customer-teal-light dark:bg-customer-teal/20 flex items-center justify-center">
                  {document.mimeType?.includes('pdf') ? (
                    <span className="text-customer-teal">PDF</span>
                  ) : (
                    <span className="text-customer-teal">IMG</span>
                  )}
                </div>
                <div className="overflow-hidden">
                  <p className="text-sm font-medium text-app-text-light dark:text-app-text truncate">
                    {document.fileName}
                  </p>
                  <p className="text-xs text-app-text-secondary">
                    Uploaded {new Date(document.uploadedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {document.verificationStatus === 'rejected' && document.rejectionReason && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-sm">
                <p className="font-medium text-red-700 dark:text-red-400">Reason for rejection:</p>
                <p className="text-red-600 dark:text-red-300">{document.rejectionReason}</p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleDocumentView(type)}
                className="flex-1 py-2.5 px-4 rounded-xl text-center text-sm font-medium bg-customer-teal-light dark:bg-customer-teal/20 text-customer-teal hover:bg-customer-teal hover:text-white transition-colors"
              >
                View Document
              </button>
              <button
                onClick={() => handleDocumentDelete(type)}
                disabled={loading}
                className="py-2.5 px-4 rounded-xl text-sm font-medium bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        ) : (
          <div>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf"
              onChange={(e) => handleDocumentUpload(type, e.target.files[0])}
              className="hidden"
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={loading}
              className="w-full py-4 px-4 rounded-xl border-2 border-dashed border-app-border-light dark:border-app-border hover:border-customer-teal dark:hover:border-customer-teal transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-customer-teal border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-app-text-secondary">Uploading...</span>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-3xl mb-2">📤</div>
                  <p className="text-sm font-medium text-app-text-light dark:text-app-text">
                    Upload {title}
                  </p>
                  <p className="text-xs text-app-text-secondary mt-1">
                    JPEG, PNG, HEIC, or PDF (max 10MB)
                  </p>
                </div>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!user) {
    return (
      <div className="flex flex-col h-full customer-friendly">
        <AppHeader title="Profile" subtitle="Your personal space" />
        <div className="flex-1 flex flex-col items-center justify-center text-sm text-app-text-secondary dark:text-app-text-secondary p-6">
          <div className="text-center bg-customer-teal-light/30 dark:bg-app-bg-secondary rounded-3xl p-8">
            <div className="text-4xl mb-3">👋</div>
            <p className="text-lg font-medium text-app-text-light dark:text-app-text mb-2">Hey there!</p>
            <p className="mb-4">Sign in to see your profile and manage your account.</p>
            <Link
              to="/login"
              className="inline-block px-6 py-3 rounded-2xl bg-gradient-to-r from-customer-teal to-customer-teal-hover text-white font-semibold shadow-lg shadow-customer-teal/30 transition-all duration-300 hover:scale-105"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full customer-friendly">
      <AppHeader title="Your Profile" subtitle="Manage your account and documents" />
      <div className="flex-1 overflow-y-auto p-4 text-sm space-y-4">
        {/* User Info Card */}
        <div className="bg-white dark:bg-app-bg-secondary rounded-3xl p-5 border border-app-border-light/50 dark:border-app-border shadow-sm">
          <h3 className="text-xs uppercase tracking-wider text-app-text-secondary dark:text-app-text-secondary mb-3">
            About You
          </h3>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-customer-accent to-customer-accent-hover rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-customer-accent/30">
              {user.name ? user.name[0].toUpperCase() : 'U'}
            </div>
            <div className="flex-1">
              {isEditingName ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-app-border-light dark:border-app-border bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text focus:outline-none focus:ring-2 focus:ring-customer-teal"
                    autoFocus
                  />
                  {nameError && <p className="text-red-500 text-xs">{nameError}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={handleNameSave}
                      disabled={nameLoading}
                      className="px-4 py-1.5 rounded-lg bg-customer-teal text-white text-xs font-medium disabled:opacity-50"
                    >
                      {nameLoading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingName(false);
                        setName(user.name);
                        setNameError('');
                      }}
                      className="px-4 py-1.5 rounded-lg bg-app-bg-light dark:bg-app-bg text-app-text-secondary text-xs font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-lg text-app-text-light dark:text-app-text">
                    {user.name}
                  </div>
                  <button
                    onClick={() => setIsEditingName(true)}
                    className="p-1.5 rounded-lg hover:bg-app-bg-light dark:hover:bg-app-bg transition-colors"
                    title="Edit name"
                  >
                    <svg className="w-4 h-4 text-app-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </div>
              )}
              <div className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">
                {user.email}
              </div>
            </div>
          </div>

          {/* Phone number (required for dealership follow-up) */}
          <div className="mt-4 pt-4 border-t border-app-border-light/50 dark:border-app-border">
            <div className="text-xs uppercase tracking-wider text-app-text-secondary dark:text-app-text-secondary mb-2">
              Phone Number
            </div>
            {isEditingPhone ? (
              <div className="space-y-2">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 555-0100"
                  className="w-full px-3 py-2 rounded-xl border border-app-border-light dark:border-app-border bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text focus:outline-none focus:ring-2 focus:ring-customer-teal"
                  autoFocus
                />
                {phoneError && <p className="text-red-500 text-xs">{phoneError}</p>}
                <div className="flex gap-2">
                  <button
                    onClick={handlePhoneSave}
                    disabled={phoneLoading}
                    className="px-4 py-1.5 rounded-lg bg-customer-teal text-white text-xs font-medium disabled:opacity-50"
                  >
                    {phoneLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingPhone(false);
                      setPhone(user.phone || '');
                      setPhoneError('');
                    }}
                    className="px-4 py-1.5 rounded-lg bg-app-bg-light dark:bg-app-bg text-app-text-secondary text-xs font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-app-text-light dark:text-app-text">
                  {user.phone || <span className="text-app-warning">Not set — add your phone number</span>}
                </span>
                <button
                  onClick={() => setIsEditingPhone(true)}
                  className="p-1.5 rounded-lg hover:bg-app-bg-light dark:hover:bg-app-bg transition-colors"
                  title="Edit phone number"
                >
                  <svg className="w-4 h-4 text-app-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          {user.role && (
            <div className="mt-3 inline-block px-3 py-1.5 rounded-full bg-customer-teal-light text-customer-teal text-xs font-medium">
              {user.role.replace('_', ' ')}
            </div>
          )}
        </div>

        {/* Password Change Card */}
        {user.authProvider === 'local' && (
          <div className="bg-white dark:bg-app-bg-secondary rounded-3xl p-5 border border-app-border-light/50 dark:border-app-border shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-xs uppercase tracking-wider text-app-text-secondary dark:text-app-text-secondary">
                Security
              </h3>
              {!showPasswordForm && (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="text-sm text-customer-teal hover:text-customer-teal-hover font-medium"
                >
                  Change Password
                </button>
              )}
            </div>

            {showPasswordForm && (
              <form onSubmit={handlePasswordChange} className="mt-4 space-y-3">
                {passwordError && (
                  <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                    {passwordError}
                  </div>
                )}
                {passwordSuccess && (
                  <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm">
                    {passwordSuccess}
                  </div>
                )}
                <div>
                  <label className="block text-xs text-app-text-secondary mb-1">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-app-border-light dark:border-app-border bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text focus:outline-none focus:ring-2 focus:ring-customer-teal"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-app-text-secondary mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-app-border-light dark:border-app-border bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text focus:outline-none focus:ring-2 focus:ring-customer-teal"
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="block text-xs text-app-text-secondary mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-app-border-light dark:border-app-border bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text focus:outline-none focus:ring-2 focus:ring-customer-teal"
                    required
                    minLength={6}
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="flex-1 py-2.5 rounded-xl bg-customer-teal text-white font-medium disabled:opacity-50"
                  >
                    {passwordLoading ? 'Updating...' : 'Update Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                    }}
                    className="px-4 py-2.5 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-secondary font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Documents Section */}
        <div className="space-y-3">
          <h3 className="text-xs uppercase tracking-wider text-app-text-secondary dark:text-app-text-secondary px-1">
            Your Documents
          </h3>
          <p className="text-xs text-app-text-secondary px-1">
            Upload your license and insurance for faster test drive verification at dealerships.
          </p>
          <DocumentCard
            type="license"
            document={licenseDoc}
            loading={docLoading.license}
            error={docError.license}
            inputRef={licenseInputRef}
          />
          <DocumentCard
            type="insurance"
            document={insuranceDoc}
            loading={docLoading.insurance}
            error={docError.insurance}
            inputRef={insuranceInputRef}
          />
        </div>

        {/* Appearance Settings Card */}
        <div className="bg-white dark:bg-app-bg-secondary rounded-3xl p-5 border border-app-border-light/50 dark:border-app-border shadow-sm">
          <ThemeToggle />
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="mt-4 w-full py-4 rounded-2xl bg-app-error hover:bg-app-error-hover text-white font-semibold transition-all duration-300"
        >
          Log Out
        </button>

        {/* Delete account (customers) */}
        {user?.role === 'customer' && (
          <div className="mt-2 bg-white dark:bg-app-bg-secondary rounded-3xl p-5 border border-red-200 dark:border-red-900/40 shadow-sm space-y-3">
            <div>
              <div className="font-semibold text-red-600">Delete account</div>
              <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                Permanently deletes your account and your driver&apos;s license and insurance files, and cancels your
                upcoming test drives. This cannot be undone.
              </p>
            </div>
            {!deleteOpen ? (
              <button
                onClick={() => setDeleteOpen(true)}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-red-50 dark:bg-red-900/20 text-red-600"
              >
                Delete my account
              </button>
            ) : (
              <form onSubmit={handleDeleteAccount} className="space-y-3">
                <label className="block text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                  {usesPassword ? 'Enter your password to confirm' : 'Type DELETE to confirm'}
                  <input
                    type={usesPassword ? 'password' : 'text'}
                    value={deleteInput}
                    onChange={(e) => setDeleteInput(e.target.value)}
                    autoFocus
                    className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-sm outline-none"
                  />
                </label>
                {deleteError && <p className="text-xs text-red-600">{deleteError}</p>}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={deleting || (usesPassword ? !deleteInput : deleteInput !== 'DELETE')}
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-red-600 text-white disabled:opacity-50"
                  >
                    {deleting ? 'Deleting…' : 'Permanently delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDeleteOpen(false); setDeleteInput(''); setDeleteError(''); }}
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-app-bg-light dark:bg-app-bg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileScreen;
